"use client";

import { useRef, type CSSProperties, type ReactNode, type RefObject } from "react";
import * as Dialog from "@radix-ui/react-dialog";

type ModalSize = "sm" | "md" | "lg";
type ModalPlacement = "center" | "top";

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  ariaLabel?: string;
  children: ReactNode;
  size?: ModalSize;
  /** Accessible name of the close control, in the surface's language. */
  closeLabel?: string;
  /**
   * The element to focus on open. Without it Radix focuses the first
   * focusable node, which is the close button (it precedes the children).
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * `top` anchors the dialog under the top edge instead of centring it. Use it
   * for a dialog that focuses a text field on open (search): the phone
   * keyboard rises at once and covers the bottom of the layout viewport, which
   * is where a centred dialog's lower half sits.
   */
  placement?: ModalPlacement;
}

const MAX_WIDTH_MAP: Record<ModalSize, number> = { sm: 320, md: 480, lg: 640 };
const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--surface-overlay-dark)",
  zIndex: 9999,
  padding: "var(--spacing-md)",
};
const TOP_INSET = "var(--spacing-xxxl)";
const topOverlayStyle: CSSProperties = {
  ...overlayStyle,
  alignItems: "flex-start",
  paddingTop: TOP_INSET,
};
const dialogStyle: CSSProperties = {
  background: "var(--surface-raised)",
  color: "var(--text-primary)",
  borderRadius: "var(--radius-lg)",
  padding: "var(--spacing-lg)",
  width: "100%",
  minWidth: 0,
  maxHeight: "calc(100dvh - 2 * var(--spacing-md))",
  display: "flex",
  flexDirection: "column",
  boxShadow: "var(--shadow-elevation-high)",
};
const closeButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "var(--spacing-xxl)",
  height: "var(--spacing-xxl)",
  borderRadius: "var(--radius-full)",
  border: "none",
  background: "var(--surface-pressed)",
  color: "var(--text-secondary)",
  fontSize: "var(--typography-title-font-size)",
  cursor: "pointer",
  flexShrink: 0,
};

export default function Modal({
  visible,
  onClose,
  title,
  ariaLabel,
  children,
  size = "md",
  closeLabel = "Close",
  initialFocus,
  placement = "center",
}: ModalProps) {
  // Existing callers control the modal without a Dialog.Trigger. Restore their
  // actual opener when dismissed, including when the close comes from state.
  const opener = useRef<HTMLElement | null>(null);
  return (
    <Dialog.Root
      open={visible}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay style={placement === "top" ? topOverlayStyle : overlayStyle}>
          <Dialog.Content
            aria-describedby={undefined}
            style={{
              ...dialogStyle,
              maxWidth: MAX_WIDTH_MAP[size],
              ...(placement === "top" && {
                maxHeight: `calc(100dvh - ${TOP_INSET} - var(--spacing-md))`,
              }),
            }}
            onOpenAutoFocus={(event) => {
              opener.current = document.activeElement as HTMLElement;
              if (initialFocus?.current) {
                event.preventDefault();
                initialFocus.current.focus();
              }
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (opener.current?.isConnected) opener.current.focus();
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "var(--spacing-md)",
                gap: "var(--spacing-sm-md)",
              }}
            >
              <Dialog.Title
                className={title ? "text-title" : "sr-only"}
                style={{ margin: 0, minWidth: 0, overflowWrap: "anywhere", flex: 1 }}
              >
                {title || ariaLabel || "Dialog"}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" aria-label={closeLabel} style={closeButtonStyle}>
                  ×
                </button>
              </Dialog.Close>
            </div>
            <div
              style={{ overflowY: "auto", overscrollBehavior: "contain", minHeight: 0, flex: 1 }}
            >
              {children}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
